import {
  CheckboxGroupField,
  EmptyState,
  Field,
  SkeletonLoader,
  TOption,
} from '@payaca/ambrosia';
import { CurrencyValue as TCurrencyValue } from '@payaca/types/currencyTypes';
import { useIsMutating } from '@tanstack/react-query';
import { FC, ReactNode, useEffect, useId, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import useGetMyAccountGrants from '@/api/queries/me/useGetMyAccountGrants';
import AutoSaveForm, {
  AutoSaveFormHandle,
} from '@/ui/components/autoSaveForm/AutoSaveForm';

import GrantOptionLabel from './GrantOptionLabel';

type CatalogueGrant = {
  id: string;
  name: string;
  description?: string | null;
  availableAmount: TCurrencyValue;
};

type AttachedGrant = CatalogueGrant & {
  amount: TCurrencyValue;
  source?: { id: string } | null;
};

type FormValues = { grantIds: string[] };

const isSameSelection = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

type GrantsPickerProps = {
  attached: AttachedGrant[];
  /** `grantLineId` is generated here, so the line can be removed before the add lands. */
  onAdd: (args: { catalogueGrantId: string; grantLineId: string }) => void;
  onRemove: (grantLineId: string) => void;
  /** The scope the document's mutations queue in. */
  mutationScopeId: string;
  helperText?: ReactNode;
};

/**
 * Grants on an estimate or proposal. Attached grants whose catalogue entry has
 * gone are listed after the catalogue, so they can still be removed.
 */
const GrantsPicker: FC<GrantsPickerProps> = (props) => {
  const { attached } = props;
  const { grants: catalogue, isLoading } = useGetMyAccountGrants();

  if (!isLoading && catalogue.length === 0 && attached.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          iconName="link-broken-01.3"
          text="No grants in your account catalogue yet."
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      {isLoading ? (
        <GrantOptionsSkeleton />
      ) : (
        <GrantsForm catalogue={catalogue} {...props} />
      )}
    </div>
  );
};

// Mirrors a loaded option: the group field's padded box, a checkbox, then a
// body-text row and a supporting-text row.
const GrantOptionsSkeleton: FC = () => {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <SkeletonLoader className="size-4 shrink-0 rounded" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Matches the Text line height so the skeleton lines up */}
        <div className="flex h-(--typography-body-line-height) items-center justify-between gap-3">
          <SkeletonLoader className="h-4 w-40" />
          <SkeletonLoader className="h-4 w-16" />
        </div>
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Matches the Text line height so the skeleton lines up */}
        <div className="flex h-(--typography-supporting-line-height) items-center">
          <SkeletonLoader className="h-3 w-56" />
        </div>
      </div>
    </div>
  );
};

/** Mounts once the catalogue has loaded, which seeds the form's selection. */
const GrantsForm: FC<GrantsPickerProps & { catalogue: CatalogueGrant[] }> = (
  props
) => {
  const { catalogue, attached, onAdd, onRemove, mutationScopeId, helperText } =
    props;
  const helperTextId = useId();

  const catalogueIds = new Set(catalogue.map((grant) => grant.id));
  const items = [
    ...catalogue.map((grant) => {
      const line = attached.find(
        (candidate) => candidate.source?.id === grant.id
      );
      return {
        value: grant.id,
        grant: line ?? grant,
        attached: line,
        catalogueGrantId: grant.id,
      };
    }),
    ...attached
      .filter((line) => !line.source || !catalogueIds.has(line.source.id))
      .map((line) => ({
        value: line.id,
        grant: line,
        attached: line,
        catalogueGrantId: undefined,
      })),
  ];
  const savedGrantIds = items
    .filter((item) => item.attached)
    .map((item) => item.value);

  const methods = useForm<FormValues>({
    defaultValues: { grantIds: savedGrantIds },
  });
  const formRef = useRef<AutoSaveFormHandle>(null);
  const submittedGrantIdsRef = useRef(savedGrantIds);
  // The line ID of each grant added here, which the cache may not hold yet.
  const addedLineIdsRef = useRef(new Map<string, string>());

  // Once every save has settled, take the server's selection. That undoes a
  // failed save, and picks up grants changed elsewhere. Any queued mutation on
  // the document counts, because the cache is only written by the last one.
  const isSaving =
    useIsMutating({
      predicate: (mutation) => mutation.options.scope?.id === mutationScopeId,
    }) > 0;
  const savedGrantIdsKey = savedGrantIds.join(',');
  useEffect(() => {
    if (isSaving) return;
    const formGrantIds = methods.getValues('grantIds');
    if (!isSameSelection(formGrantIds, submittedGrantIdsRef.current)) return;
    if (isSameSelection(formGrantIds, savedGrantIds)) return;

    submittedGrantIdsRef.current = savedGrantIds;
    methods.reset({ grantIds: savedGrantIds });
    formRef.current?.resetBaseline();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- savedGrantIdsKey stands in for savedGrantIds
  }, [isSaving, savedGrantIdsKey, methods]);

  const handleSubmit = ({ grantIds }: FormValues) => {
    const previous = submittedGrantIdsRef.current;
    submittedGrantIdsRef.current = grantIds;

    for (const item of items) {
      const isSelected = grantIds.includes(item.value);
      if (isSelected === previous.includes(item.value)) continue;

      if (isSelected) {
        // A grant whose catalogue entry has gone cannot be added back.
        if (!item.catalogueGrantId) continue;
        const grantLineId = crypto.randomUUID();
        addedLineIdsRef.current.set(item.value, grantLineId);
        onAdd({ catalogueGrantId: item.catalogueGrantId, grantLineId });
      } else {
        const grantLineId =
          addedLineIdsRef.current.get(item.value) ?? item.attached?.id;
        addedLineIdsRef.current.delete(item.value);
        if (grantLineId) onRemove(grantLineId);
      }
    }
  };

  const options: TOption[] = items.map((item) => ({
    value: item.value,
    label: (
      <GrantOptionLabel
        name={item.grant.name}
        description={item.grant.description}
        availableAmount={item.grant.availableAmount}
        appliedAmount={item.attached?.amount}
      />
    ),
  }));

  return (
    <AutoSaveForm
      ref={formRef}
      methods={methods}
      debounceMs={0}
      onSubmit={handleSubmit}
      aria-label="Grants"
      aria-describedby={helperText ? helperTextId : undefined}
      aria-busy={isSaving}
    >
      <Field name="grantIds">
        {helperText && (
          <div id={helperTextId}>
            <Field.HelperText>{helperText}</Field.HelperText>
          </div>
        )}
        <span role="status" className="sr-only">
          {isSaving ? 'Saving grants' : ''}
        </span>
        <Controller<FormValues, 'grantIds'>
          name="grantIds"
          render={({ field: { value, onChange } }) => (
            <CheckboxGroupField
              emphasised
              options={options}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </Field>
    </AutoSaveForm>
  );
};

export default GrantsPicker;
